import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Layers, 
  ShieldCheck, 
  FileCheck, 
  CheckCircle2, 
  Users, 
  Sparkles,
  Smartphone,
  Moon,
  Sun,
  TrendingUp,
  AlertTriangle,
  RotateCcw,
  Minus,
  Plus,
  Delete,
  Check
} from 'lucide-react';

// Custom Rupee Icon
const RupeeIcon = ({ className = "w-5 h-5" }) => (
  <span className={`font-semibold select-none ${className}`} style={{ fontFamily: 'sans-serif' }}>₹</span>
);

// App-Specific Mockup Wrapper to emulate the mobile app UI from the video
const MobileMockup = ({ children, titleTop, titleBottom, showBottomNav, onNext }) => (
  <div className="w-[300px] h-[550px] bg-[#f0f0f5] rounded-[2.5rem] border-[8px] border-slate-800 shadow-2xl relative overflow-hidden flex flex-col font-sans shrink-0 mx-auto">
    {/* Fake Notch */}
    <div className="absolute top-0 inset-x-0 h-5 bg-slate-800 rounded-b-2xl w-1/2 mx-auto z-50"></div>
    
    {/* App Header */}
    <div className="bg-gradient-to-b from-[#e2e2fa] to-[#f0f0f5] pt-10 pb-2 px-5 shrink-0">
      {titleTop && <h3 className="text-lg font-light text-[#1a1a5c] leading-tight">{titleTop}</h3>}
      {titleBottom && <h4 className="text-3xl font-semibold text-[#1a1a5c]">{titleBottom}</h4>}
    </div>
    
    {/* App Body */}
    <div className="flex-1 overflow-y-auto px-4 pb-20 scrollbar-hide relative z-10 flex flex-col gap-3">
      {children}
    </div>

    {/* Video-Style Bottom Navigation */}
    {showBottomNav && (
      <div className="absolute bottom-0 inset-x-0 px-4 py-3 bg-gradient-to-t from-[#f0f0f5] via-[#f0f0f5] to-transparent flex justify-center items-center z-20 gap-4">
        <button className="w-10 h-10 rounded-full border border-[#1a1a5c] flex items-center justify-center text-[#1a1a5c] bg-white shadow-sm hover:bg-slate-50 transition-colors">
          <ChevronLeft size={20} />
        </button>
        <button 
          onClick={onNext}
          className="bg-[#1a1a5c] hover:bg-[#0f0f42] text-white px-8 py-2.5 rounded-full font-bold flex items-center gap-2 shadow-lg transition-transform active:scale-95"
        >
          Next <ChevronRight size={18} />
        </button>
      </div>
    )}
  </div>
);

// Video-Style Input Component
const AppInput = ({ label, value, onChange, type = "text", rightIcon }) => (
  <div className="bg-[#cfcfed]/60 rounded-xl p-2.5 shadow-inner relative border border-white/20 flex flex-col justify-center min-h-[56px]">
    <span className="text-[10px] text-[#4a4a8a] absolute top-1.5 left-3 font-semibold">{label}</span>
    <div className="flex items-center justify-between mt-3 px-1">
      <input 
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-none text-[#1a1a5c] font-semibold text-sm focus:outline-none placeholder-[#8a8aa8]" 
      />
      {rightIcon && <div className="text-[#4a4a8a]">{rightIcon}</div>}
    </div>
  </div>
);

// DiGiOPS Logo
const DiGiOPSLogo = ({ className = "h-12" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <svg className="w-10 h-10" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 50C20 33.4315 33.4315 20 50 20C58.2843 20 65.7843 23.3579 71.2132 28.7868L57.0711 42.9289C55.2635 41.1213 52.7635 40 50 40C44.4772 40 40 44.4772 40 50" stroke="#F97316" strokeWidth="12" strokeLinecap="round" />
      <path d="M80 50C80 66.5685 66.5685 80 50 80C41.7157 80 34.2157 76.6421 28.7868 71.2132L42.9289 57.0711C44.7365 58.8787 47.2365 60 50 60C55.5228 60 60 55.5228 60 50" stroke="#10B981" strokeWidth="12" strokeLinecap="round" />
      <circle cx="50" cy="50" r="10" fill="#1E40AF" />
    </svg>
    <div className="flex flex-col">
      <span className="text-xl font-black tracking-wider text-slate-800 dark:text-white leading-none">
        DiG<span className="text-orange-500">i</span>OPS
      </span>
      <span className="text-[9px] uppercase tracking-widest text-emerald-500 font-bold">Smart Manufacturing</span>
    </div>
  </div>
);

export default function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  // Interactive Calculators
  const [calcWorkers, setCalcWorkers] = useState(25);

  const [calcWeeklyHoursSpent, setCalcWeeklyHoursSpent] = useState(10);

  const [calcHourlyRate, setCalcHourlyRate] = useState(200); 
  
  // Procurement State
  const [procurementItems, setProcurementItems] = useState([
    { id: 'PR-2026-01', item: 'Boiler Plate (Grade A)', qty: '12 Tons', dept: 'Production', status: 'Pending Approval' },
    { id: 'PR-2026-02', item: 'Weld Electrodes E7018', qty: '50 Pkts', dept: 'Welding', status: 'Approved' },
    { id: 'PR-2026-03', item: 'Pickling Solvent Acid', qty: '200 Ltr', dept: 'Surface Prep', status: 'Draft' },
  ]);

  // Stores State
  const [tools, setTools] = useState([
    { name: 'Ultrasonic Flowmeter', serial: '21032948', status: 'Overdue', daysLeft: -12 },
    { name: 'Digital Vernier Caliper', serial: '22077224', status: 'Warning', daysLeft: 4 },
    { name: 'Pressure Gauge PG-40', serial: '22127456', status: 'Calibrated', daysLeft: 180 },
  ]);
  const [calibrationEmailSent, setCalibrationEmailSent] = useState(false);

  // Quality State (Mimicking the video form)
  const [reportForm, setReportForm] = useState({
    tagNumber: 'C000TEST-026',
    seamNumber: 'CS01',
    reportNumber: 'CISPL/C000TEST-026/RT-',
    thickness: '10.5',
    outerDiameter: '56.0'
  });
  const [reportStatus, setReportStatus] = useState('idle'); // idle, generating, done

  // HR State
  const [workers, setWorkers] = useState([
    { id: 1, name: 'Devnath Chauhan', role: 'Welder', status: 'Present', hrs: 8.5, rate: 250 },
    { id: 2, name: 'Md Salik', role: 'Press Operator', status: 'Absent', hrs: 0, rate: 200 },
    { id: 3, name: 'Janjir Ansari', role: 'Welder', status: 'Present', hrs: 9.0, rate: 280 },
    { id: 4, name: 'Abhimanyu Sharma', role: 'Fitter', status: 'Present', hrs: 8.0, rate: 220 },
  ]);
  const [selectedWorkerPayroll, setSelectedWorkerPayroll] = useState(null);

  // Security Module (Mimicking Screenshot precisely)
  const [pinInput, setPinInput] = useState('');
  const [isPinAuthorized, setIsPinAuthorized] = useState(null);

  // Calculate Indian Rupee ROI
  const calculateSavings = () => {
    const manualHoursYearly = calcWeeklyHoursSpent * 52 * calcWorkers;
    const savedHoursYearly = manualHoursYearly * 0.75;
    const annualSavings = savedHoursYearly * calcHourlyRate;

    return {
      hoursSaved: Math.round(savedHoursYearly).toLocaleString('en-IN'),
      moneySaved: Math.round(annualSavings).toLocaleString('en-IN'),
      paperSaved: Math.round(calcWorkers * 120).toLocaleString('en-IN') 
    };
  };

  const currentSavings = calculateSavings();

  const handleGenerateReport = () => {
    setReportStatus('generating');
    setTimeout(() => {
      setReportStatus('done');
    }, 1800);
  };

  const verifyPin = (pinToTest = pinInput) => {
    const currentPin = typeof pinToTest === 'string' ? pinToTest : pinInput;
    if (currentPin === '123456') setIsPinAuthorized(true);
    else setIsPinAuthorized(false);
    setTimeout(() => {
        setPinInput('');
        setIsPinAuthorized(null);
    }, 2000);
  };

  // Slides Array
  const slidesData = [
    {
      title: "The Future of Custom Manufacturing ERP",
      subtitle: "Bespoke. Mobile-First. Audit-Traceable.",
      category: "Intro",
      content: (
        <div className="flex flex-col md:flex-row items-center gap-8 h-full justify-center">
          <div className="flex-1 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400 font-medium text-xs">
              <Sparkles className="w-4.5 h-4.5 text-orange-500 animate-pulse" />
              DiGiOPS Enterprise Pitch
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-800 dark:text-white leading-tight">
              Bespoke Digital Operations for <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-emerald-500 to-blue-600">Manufacturing & Fabrication</span>
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-300">
              Stop fitting your unique shop-floor processes into rigid, expensive off-the-shelf software. Welcome to a modular ecosystem designed to digitalize quality control, streamline inventory, and track workforce productivity.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700/50">
                <Smartphone className="w-5 h-5 text-emerald-500" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Mobile-First UI</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700/50">
                <ShieldCheck className="w-5 h-5 text-orange-500" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">PIN Audit Traceability</span>
              </div>
            </div>
          </div>
          <div className="flex-1 w-full max-w-md bg-gradient-to-tr from-slate-900 to-slate-800 p-8 rounded-3xl border border-slate-700 shadow-2xl relative overflow-hidden text-white flex flex-col justify-between aspect-video min-h-[320px]">
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-orange-500/10 rounded-full blur-2xl"></div>
            <div className="flex justify-between items-start">
              <DiGiOPSLogo className="scale-90 origin-left" />
              <span className="bg-emerald-500/20 text-emerald-400 text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded-full">Active V1.0</span>
            </div>
            <div className="space-y-3">
              <div className="text-xs text-slate-400 font-mono">CLIENT DEPLOYMENT PROFILE:</div>
              <h3 className="text-xl font-bold">CISPL Fabrication Unit</h3>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="border-l-2 border-emerald-500 pl-3">
                  <div className="text-2xl font-black text-emerald-400">22%</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Paperwork Reduced</div>
                </div>
                <div className="border-l-2 border-orange-500 pl-3">
                  <div className="text-2xl font-black text-orange-400">&lt; 5m</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">Report Drafting Time</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Inflexible ERP Trap",
      subtitle: "Why Traditional ERP Solutions Fail Manufacturing Units",
      category: "Problem",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 h-full items-center">
          <div className="space-y-5">
            <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Why One-Size-Fits-All Fails</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Traditional ERP solutions assume static, administrative workflows. But shop-floors are alive, changing, and high-frequency environments.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 rounded-2xl">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-slate-800 dark:text-slate-200">Heavy Customization Overhead</h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Modifying a standard package to fit specific shop operations costs 3x the license fee and takes months.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-orange-500/5 dark:bg-orange-500/10 border border-orange-500/20 rounded-2xl">
                <Smartphone className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-slate-800 dark:text-slate-200">Clunky Desktop Interfaces</h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Engineers and operators refuse to walk back to desktop terminals to log measurements or weld plans.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/40 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 flex flex-col justify-center">
            <h4 className="text-sm uppercase font-bold tracking-widest text-slate-400 mb-4">THE MANUFACTURER'S COST MATRIX</h4>
            <div className="space-y-4">
              <div className="p-4 bg-white dark:bg-slate-800 rounded-xl shadow-xs border border-slate-100 dark:border-slate-700/40">
                <div className="flex justify-between text-sm font-semibold text-slate-600 dark:text-slate-300">
                  <span>Customization Cost</span>
                  <span className="text-red-500">200% - 300% of Base</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full mt-2 overflow-hidden">
                  <div className="bg-red-500 h-full w-[85%]"></div>
                </div>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-xl shadow-xs border border-slate-100 dark:border-slate-700/40">
                <div className="flex justify-between text-sm font-semibold text-slate-600 dark:text-slate-300">
                  <span>Average Report Delay</span>
                  <span className="text-orange-500">3-5 Days</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full mt-2 overflow-hidden">
                  <div className="bg-orange-500 h-full w-[70%]"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Power of the Starting Four Modules",
      subtitle: "Bespoke digital capability for your highest-impact divisions",
      category: "Modules Hub",
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full items-center">
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Our Phase-1 Core Architecture</h3>
            <p className="text-slate-600 dark:text-slate-300">
              We identified the four operational bottlenecks that cost manufacturing companies the most in wasted hours, penalty fees, and administrative burden.
            </p>
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/25 rounded-2xl flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
              <div>
                <span className="font-bold text-emerald-800 dark:text-emerald-400 text-sm">Deploy and train within 5 weeks</span>
                <p className="text-xs text-slate-500 dark:text-slate-300">Fast baseline setup, immediate training, and visible ROI from month one.</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Procurement */}
            <div className="p-4 bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-xs hover:border-orange-500/30 transition-all cursor-pointer" onClick={() => setCurrentSlide(3)}>
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-500/10 text-orange-500 rounded-xl flex items-center justify-center mb-3">
                <RupeeIcon className="text-orange-500 text-lg" />
              </div>
              <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm">1. Procurement</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Requisition to Payment flows. Instant approval logs and vendor database.</p>
            </div>
            {/* Stores */}
            <div className="p-4 bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-xs hover:border-emerald-500/30 transition-all cursor-pointer" onClick={() => setCurrentSlide(4)}>
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-500/10 text-emerald-500 rounded-xl flex items-center justify-center mb-3">
                <Layers className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm">2. Inventory & Stores</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Calibration warnings, real-time material allocations and stock logs.</p>
            </div>
            {/* Quality */}
            <div className="p-4 bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-xs hover:border-blue-500/30 transition-all cursor-pointer" onClick={() => setCurrentSlide(5)}>
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/10 text-blue-500 rounded-xl flex items-center justify-center mb-3">
                <FileCheck className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm">3. Quality Reports</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Weld Plans, heat charts, inspection schedules and quick digital certificates.</p>
            </div>
            {/* HR Attendance */}
            <div className="p-4 bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-xs hover:border-indigo-500/30 transition-all cursor-pointer" onClick={() => setCurrentSlide(6)}>
              <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-500/10 text-indigo-500 rounded-xl flex items-center justify-center mb-3">
                <Users className="w-5 h-5" />
              </div>
              <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm">4. Attendance & Pay</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Role-based job logging, automated timesheets and payslips.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Module 1: Purchase & Procurement Flow",
      subtitle: "Never lose trace of a requisition from request to billing",
      category: "Procurement Demo",
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full items-center">
          <div className="space-y-4">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-500/10 text-orange-500 rounded-xl flex items-center justify-center mb-2">
              <RupeeIcon className="text-orange-500 text-lg" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Eliminating the Paper Requisition Trail</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Procurement processes inside heavy fabrication are often tied to paper slips. When a welder needs a plate or specific electrode, it can take 3 days of running around for physical signatures. 
            </p>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 flex-shrink-0" />
                <span>Instant Purchase Requisition (PR) initiation on Mobile.</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 flex-shrink-0" />
                <span>Immediate push notifications to HODs and MDs for approval.</span>
              </li>
            </ul>
          </div>
          
          {/* MOBILE APP MOCKUP: PROCUREMENT */}
          <MobileMockup titleTop="Purchase" titleBottom="Requisitions" showBottomNav={false}>
            <div className="flex justify-between items-center px-1 pb-1">
               <span className="text-xs font-bold text-[#8a8aa8] uppercase tracking-widest">Active Queue</span>
               <button onClick={() => setProcurementItems([...procurementItems].map(p=>({...p, status: p.status === 'Approved' ? 'Pending Approval' : p.status})))} className="p-1 text-[#1a1a5c]"><RotateCcw size={14}/></button>
            </div>
            {procurementItems.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-[#e2e2fa]">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] font-bold text-[#4a4a8a] bg-[#cfcfed]/40 px-2 py-0.5 rounded-md">{item.dept}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    item.status === 'Approved' ? 'bg-[#10B981]/10 text-[#10B981]' : 
                    item.status === 'Pending Approval' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {item.status}
                  </span>
                </div>
                <h4 className="text-[#1a1a5c] font-bold text-sm leading-tight">{item.item}</h4>
                <div className="flex justify-between items-end mt-3">
                  <span className="text-[#1a1a5c] font-black text-lg">{item.qty}</span>
                  {item.status === 'Pending Approval' && (
                    <button 
                      onClick={() => setProcurementItems(procurementItems.map(p => p.id === item.id ? { ...p, status: 'Approved' } : p))}
                      className="bg-[#1a1a5c] text-white text-[10px] font-bold px-4 py-1.5 rounded-full shadow-md active:scale-95 transition-transform"
                    >
                      Approve
                    </button>
                  )}
                </div>
              </div>
            ))}
          </MobileMockup>
        </div>
      )
    },
    {
      title: "Module 2: Stores & Calibration Monitoring",
      subtitle: "Prevent costly job rejections due to uncalibrated equipment",
      category: "Stores Demo",
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full items-center">
          <div className="space-y-4">
            <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-500/10 text-emerald-500 rounded-xl flex items-center justify-center mb-2">
              <Layers className="w-5 h-5" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Intelligent Inventory & Calibration Alerts</h3>
            <p className="text-slate-600 dark:text-slate-300">
              A single welded joint failed during audit because an uncalibrated gauge was used? That can cost a manufacturer a massive penalty. 
            </p>
            <div className="p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-700/60 space-y-2">
              <h4 className="font-bold text-sm text-slate-800 dark:text-slate-200">Automated Alert Triggers</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                DiGiOPS automatically compiles all tools expiring within the next 15 days, shooting an alarm email to storekeepers and management every Monday at 8:00 AM.
              </p>
            </div>
          </div>
          
          {/* MOBILE APP MOCKUP: STORES */}
          <MobileMockup titleTop="Stores" titleBottom="Tool Calibration" showBottomNav={false}>
            <button 
              onClick={() => setCalibrationEmailSent(true)}
              className="w-full bg-[#10B981] text-white font-bold py-3 rounded-2xl shadow-[0_4px_15px_rgba(16,185,129,0.3)] mb-2 flex justify-center items-center gap-2 active:scale-95 transition-transform"
            >
              {calibrationEmailSent ? <><Check size={16}/> Alert Sent Successfully</> : "Simulate Monday 8:00 AM Alert"}
            </button>

            {tools.map((tool) => (
              <div key={tool.serial} className="bg-white rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-[#e2e2fa]">
                <div className="flex justify-between items-start mb-1.5">
                  <span className="text-[10px] font-bold text-[#8a8aa8] uppercase">S/N: {tool.serial}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    tool.status === 'Overdue' ? 'bg-red-100 text-red-600' : 
                    tool.status === 'Warning' ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'
                  }`}>
                    {tool.daysLeft < 0 ? `${Math.abs(tool.daysLeft)}d Overdue` : `${tool.daysLeft}d left`}
                  </span>
                </div>
                <h4 className="text-[#1a1a5c] font-bold text-sm leading-tight mb-3">{tool.name}</h4>
                
                {(tool.status === 'Overdue' || tool.status === 'Warning') && (
                  <button 
                    onClick={() => setTools(tools.map(t => t.serial === tool.serial ? { ...t, status: 'Calibrated', daysLeft: 365 } : t))}
                    className="w-full bg-[#e2e2fa] text-[#1a1a5c] text-[10px] font-bold py-2 rounded-xl active:scale-95 transition-transform"
                  >
                    Mark Calibrated
                  </button>
                )}
              </div>
            ))}
          </MobileMockup>
        </div>
      )
    },
    {
      title: "Module 3: Instant Quality Reports",
      subtitle: "Drastically reduce documentation time from 20 to 5 minutes",
      category: "Quality Demo",
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full items-center">
          <div className="space-y-4">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/10 text-blue-500 rounded-xl flex items-center justify-center mb-2">
              <FileCheck className="w-5 h-5" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Seamless Quality Control (QC) Generation</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Compiling weld maps, NDT inspection logs, heat charts, and pickling-passivation documents is usually done on paper, then re-typed on a laptop. It leads to transposition errors and immense delay.
            </p>
            <div className="flex gap-4 p-4 bg-blue-500/5 dark:bg-blue-500/10 border border-blue-500/20 rounded-2xl">
              <div className="flex flex-col">
                <span className="text-2xl font-black text-blue-600 dark:text-blue-400">75%</span>
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Documentation Speed-up</span>
              </div>
              <div className="border-r border-blue-500/20"></div>
              <div className="flex flex-col">
                <span className="text-2xl font-black text-blue-600 dark:text-blue-400">100%</span>
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Traceability Match</span>
              </div>
            </div>
          </div>
          
          {/* MOBILE APP MOCKUP: QUALITY FORM mimicking video perfectly */}
          <MobileMockup titleTop="Radiography" titleBottom="Parameters" showBottomNav={reportStatus === 'idle'} onNext={handleGenerateReport}>
            
            {reportStatus === 'idle' && (
              <>
                <AppInput 
                  label="Tag Number" 
                  value={reportForm.tagNumber} 
                  onChange={e => setReportForm({...reportForm, tagNumber: e.target.value})} 
                  rightIcon={<ChevronRight size={16}/>}
                />
                <AppInput 
                  label="Seam Number" 
                  value={reportForm.seamNumber} 
                  onChange={e => setReportForm({...reportForm, seamNumber: e.target.value})} 
                  rightIcon={<ChevronRight size={16}/>}
                />
                <AppInput 
                  label="Report Number" 
                  value={reportForm.reportNumber} 
                  onChange={e => setReportForm({...reportForm, reportNumber: e.target.value})} 
                />
                
                {/* Numeric Inputs simulating the +/- controls from video */}
                <div className="bg-[#cfcfed]/60 rounded-xl p-2.5 shadow-inner relative border border-white/20 flex flex-col justify-center min-h-[56px]">
                  <span className="text-[10px] text-[#4a4a8a] absolute top-1.5 left-3 font-semibold">Thickness (mm)</span>
                  <div className="flex justify-between items-center mt-3 px-1">
                    <span className="text-[#1a1a5c] font-semibold text-sm">{reportForm.thickness}</span>
                    <div className="flex items-center gap-2 text-[#4a4a8a]">
                      <button className="p-1 hover:bg-black/5 rounded"><Minus size={16} strokeWidth={3}/></button>
                      <button className="p-1 hover:bg-black/5 rounded"><Plus size={16} strokeWidth={3}/></button>
                    </div>
                  </div>
                </div>

                <div className="bg-[#cfcfed]/60 rounded-xl p-2.5 shadow-inner relative border border-white/20 flex flex-col justify-center min-h-[56px]">
                  <span className="text-[10px] text-[#4a4a8a] absolute top-1.5 left-3 font-semibold">Outer Diameter (mm)</span>
                  <div className="flex justify-between items-center mt-3 px-1">
                    <input 
                      type="text" 
                      value={reportForm.outerDiameter} 
                      onChange={e => setReportForm({...reportForm, outerDiameter: e.target.value})}
                      className="w-full bg-transparent border-none text-[#1a1a5c] font-semibold text-sm focus:outline-none placeholder-[#8a8aa8]" 
                    />
                  </div>
                </div>
              </>
            )}

            {reportStatus === 'generating' && (
              <div className="flex flex-col items-center justify-center h-[300px] gap-6">
                <div className="flex items-end justify-center gap-1.5 h-12">
                  {[1,2,3,4,5].map(i => (
                    <div 
                      key={i} 
                      className="w-2.5 bg-[#1a1a5c] rounded-sm animate-pulse" 
                      style={{ height: `${Math.random() * 80 + 20}%`, animationDelay: `${i * 0.15}s`, animationDuration: '0.8s' }}
                    ></div>
                  ))}
                </div>
                <span className="text-[#4a4a8a] font-bold text-xs tracking-widest uppercase animate-pulse">Compiling Report...</span>
              </div>
            )}

            {reportStatus === 'done' && (
              <div className="flex flex-col gap-4 animate-fadeIn">
                <div className="bg-white p-4 rounded-xl border border-emerald-500/30 shadow-lg relative overflow-hidden">
                  <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-bl font-bold">VERIFIED PDF</div>
                  <h5 className="font-mono text-[10px] font-bold text-[#1a1a5c] mb-3 border-b border-slate-100 pb-2">RT REPORT: {reportForm.reportNumber}</h5>
                  <div className="text-[11px] space-y-1.5 text-slate-600">
                    <div className="flex justify-between"><span>Tag No:</span> <span className="font-bold text-[#1a1a5c]">{reportForm.tagNumber}</span></div>
                    <div className="flex justify-between"><span>Seam No:</span> <span className="font-bold text-[#1a1a5c]">{reportForm.seamNumber}</span></div>
                    <div className="flex justify-between"><span>Thickness:</span> <span className="font-bold text-[#1a1a5c]">{reportForm.thickness} mm</span></div>
                    <div className="flex justify-between"><span>O.D.:</span> <span className="font-bold text-[#1a1a5c]">{reportForm.outerDiameter} mm</span></div>
                  </div>
                  <div className="mt-4 pt-2 border-t border-dashed border-slate-200">
                     <span className="text-[8px] text-slate-400 font-mono block text-center">Digitally signed via Cryptographic PIN Block.</span>
                  </div>
                </div>
                <button 
                  onClick={() => setReportStatus('idle')}
                  className="mx-auto text-xs text-[#4a4a8a] font-bold underline"
                >
                  Generate Another
                </button>
              </div>
            )}
          </MobileMockup>
        </div>
      )
    },
    {
      title: "Module 4: Mobile HR & Worker Attendance",
      subtitle: "Role-based time-tracking and rapid, error-free pay calculations",
      category: "HR Demo",
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full items-center">
          <div className="space-y-4">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-500/10 text-indigo-500 rounded-xl flex items-center justify-center mb-2">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Seamless Shop Floor Workforce Management</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Welders, Fitters, Grinders, and Crane operators work across complex, task-based schedules. Manual attendance registers can easily be manipulated or contain hours allocation errors.
            </p>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 flex-shrink-0" />
                <span>Task logging linked directly to active job cards.</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500 flex-shrink-0" />
                <span>Automatic shift overtime logging and validation.</span>
              </li>
            </ul>
          </div>
          
          {/* MOBILE APP MOCKUP: HR */}
          <MobileMockup titleTop="Worker Daily" titleBottom="Attendance" showBottomNav={false}>
            {workers.map((worker) => (
              <div key={worker.id} className="bg-white rounded-2xl p-3 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-[#e2e2fa] flex justify-between items-center">
                <div>
                  <h5 className="font-bold text-[#1a1a5c] text-sm leading-tight">{worker.name}</h5>
                  <p className="text-[10px] text-[#8a8aa8] mt-0.5">{worker.role} • Wage: ₹{worker.rate}/hr</p>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setWorkers(workers.map(w => w.id === worker.id ? { ...w, status: w.status === 'Present' ? 'Absent' : 'Present', hrs: w.status === 'Present' ? 0 : 8.0 } : w))}
                    className={`text-[10px] font-bold px-3 py-1.5 rounded-lg transition-colors active:scale-95 ${
                      worker.status === 'Present' ? 'bg-[#10B981]/15 text-[#10B981]' : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {worker.status}
                  </button>
                  {worker.status === 'Present' && (
                    <button 
                      onClick={() => setSelectedWorkerPayroll(worker)}
                      className="bg-[#e2e2fa] text-[#1a1a5c] font-bold text-[10px] px-2.5 py-1.5 rounded-lg active:scale-95"
                    >
                      Slip
                    </button>
                  )}
                </div>
              </div>
            ))}

            {selectedWorkerPayroll && (
              <div className="mt-4 p-4 bg-[#1a1a5c] rounded-2xl animate-fadeIn space-y-3 shadow-xl relative text-white">
                <div className="flex justify-between items-center border-b border-white/20 pb-2">
                  <h5 className="text-[10px] font-black uppercase tracking-widest text-emerald-400">INSTANT PAY SLIP</h5>
                  <button onClick={() => setSelectedWorkerPayroll(null)} className="text-[10px] text-white/60 hover:text-white"><Delete size={14}/></button>
                </div>
                <div className="text-[11px] space-y-1.5">
                  <div className="flex justify-between"><span>Name:</span> <span className="font-bold">{selectedWorkerPayroll.name}</span></div>
                  <div className="flex justify-between"><span>Role:</span> <span className="font-medium text-white/80">{selectedWorkerPayroll.role}</span></div>
                  <div className="flex justify-between"><span>Logged:</span> <span>{selectedWorkerPayroll.hrs} hrs @ ₹{selectedWorkerPayroll.rate}/hr</span></div>
                  <div className="flex justify-between border-t border-dashed border-white/30 pt-2 text-sm font-bold mt-1">
                    <span>Daily Wage:</span> 
                    <span className="text-emerald-400">₹{(selectedWorkerPayroll.hrs * selectedWorkerPayroll.rate).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            )}
          </MobileMockup>
        </div>
      )
    },
    {
      title: "Bulletproof Security: The PIN Guard Block",
      subtitle: "High-grade accountability through localized cryptographic audit logs",
      category: "Security Demo",
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full items-center">
          <div className="space-y-4">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-500/10 text-amber-500 rounded-xl flex items-center justify-center mb-2">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Next-Level Audit Accountability</h3>
            <p className="text-slate-600 dark:text-slate-300">
              For every high-integrity action on the DiGiOPS Platform, the user enters their custom 6-digit numeric PIN.
            </p>
            <div className="p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-700/60 space-y-2">
              <h4 className="font-bold text-sm text-slate-800 dark:text-slate-200">The Cryptographic Ledger Block</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Every PIN authorization transforms the log into a unique encrypted key sequence stored back-to-back.
              </p>
            </div>
          </div>
          
          {/* MOBILE APP MOCKUP: EXACT PIN PAD REPLICA */}
          <div className="w-[300px] h-[550px] bg-[#f0f0f5] rounded-[2.5rem] border-[8px] border-slate-800 shadow-2xl relative overflow-hidden flex flex-col mx-auto font-sans">
            
            {/* Top Background (Simulating blurred app behind modal) */}
            <div className="flex-1 bg-gradient-to-b from-[#b8c6d9] to-[#d4e0eb] flex items-center justify-center opacity-60 pb-40">
                <span className="text-[#1a1a5c] font-bold text-xl blur-[1px]">Spot Details</span>
            </div>
            
            {/* Modal Overlay Bottom Section */}
            <div className="absolute bottom-0 inset-x-0 flex flex-col z-20">
               
               {/* ENTER YOUR PIN Dialog */}
               <div className="bg-white rounded-t-[2rem] rounded-b-none pt-7 pb-6 px-4 flex flex-col items-center shadow-[0_-10px_40px_rgba(0,0,0,0.1)] z-20 relative border-b border-slate-100">
                  <h3 className="text-[#0A1172] font-bold text-sm tracking-wide uppercase mb-6">Enter Your PIN</h3>
                  
                  {/* 6 Box Input layout */}
                  <div className="flex gap-2 justify-center mb-6 w-full px-2">
                     {[...Array(6)].map((_, i) => (
                        <div key={i} className={`w-[38px] h-11 border-2 rounded-lg flex items-center justify-center text-2xl font-bold transition-colors ${pinInput.length === i ? 'border-[#1A1A5C] bg-white' : 'border-slate-200 bg-slate-50'} text-[#1A1A5C]`}>
                           {pinInput[i] ? '•' : ''}
                        </div>
                     ))}
                  </div>

                  <div className="flex justify-between w-full gap-3 px-1">
                     <button className="flex-1 bg-slate-50 border border-slate-200 text-[#0A1172] font-bold text-[10px] py-3.5 rounded-full hover:bg-slate-100 transition-colors shadow-sm">USE BIOMETRIC</button>
                     <button onClick={() => verifyPin(pinInput)} className="flex-1 bg-[#0A1172] text-white font-bold text-[10px] py-3.5 rounded-full shadow-md hover:bg-[#050840] transition-colors">SUBMIT PIN</button>
                  </div>

                  {/* Feedback Tags */}
                  <div className="absolute bottom-1 w-full text-center">
                    {isPinAuthorized === true && <span className="text-[10px] text-emerald-500 font-bold animate-fadeIn">✓ Hash Signed</span>}
                    {isPinAuthorized === false && <span className="text-[10px] text-red-500 font-bold animate-fadeIn">✗ Invalid Code</span>}
                  </div>
               </div>

               {/* Light Theme Keypad Area */}
               <div className="bg-slate-50 flex flex-col px-6 pt-5 pb-8 z-10">
                  <div className="grid grid-cols-3 gap-y-3 gap-x-4 max-w-[240px] mx-auto w-full">
                     {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                        <button 
                           key={num} 
                           onClick={() => {
                             if (pinInput.length < 6) {
                               const newPin = pinInput + num;
                               setPinInput(newPin);
                               if (newPin.length === 6) verifyPin(newPin);
                             }
                           }}
                           className="bg-white hover:bg-slate-100 active:scale-95 text-[#1A1A5C] text-xl font-medium py-3 text-center rounded-xl border border-slate-200 shadow-sm transition-all"
                        >
                           {num}
                        </button>
                     ))}
                     <button 
                        onClick={() => {
                          setPinInput('');
                          setIsPinAuthorized(null);
                        }}
                        className="bg-red-50 text-red-600 hover:bg-red-100 active:scale-95 font-semibold py-3 text-center rounded-xl border border-red-100 shadow-sm text-sm flex items-center justify-center transition-all"
                     >
                        Clear
                     </button>
                     <button 
                        onClick={() => {
                          if (pinInput.length < 6) {
                            const newPin = pinInput + '0';
                            setPinInput(newPin);
                            if (newPin.length === 6) verifyPin(newPin);
                          }
                        }}
                        className="bg-white hover:bg-slate-100 active:scale-95 text-[#1A1A5C] text-xl font-medium py-3 text-center rounded-xl border border-slate-200 shadow-sm transition-all"
                     >
                        0
                     </button>
                     <button 
                        onClick={() => verifyPin(pinInput)}
                        className="bg-[#10B981] hover:bg-[#059669] active:scale-95 text-white font-bold py-3 text-[11px] uppercase tracking-wider text-center rounded-xl shadow-sm flex items-center justify-center transition-all"
                     >
                        Done
                     </button>
                  </div>
               </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Tangible ROI Business Case",
      subtitle: "Calculate direct annual operational savings with DiGiOPS Platform",
      category: "ROI Calculator",
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full items-center">
          <div className="space-y-5">
            <h3 className="text-2xl font-bold text-slate-800 dark:text-white">Hard Operational ROI for COOs</h3>
            <p className="text-slate-600 dark:text-slate-300">
              Technology shouldn't just look slick—it needs to yield visible, direct operational savings. By streamlining documentation speed and eliminating material tracking oversights, the monetary gains are massive.
            </p>

            {/* Config Sliders */}
            <div className="space-y-4 bg-slate-50 dark:bg-slate-800/30 p-5 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div>
                <div className="flex justify-between text-xs font-bold text-slate-500 mb-1.5">
                  <span>ACTIVE WORKFORCE SIZE</span>
                  <span className="text-orange-500">{calcWorkers} Members</span>
                </div>
                <input 
                  type="range" 
                  min="10" 
                  max="500" 
                  value={calcWorkers} 
                  onChange={(e) => setCalcWorkers(parseInt(e.target.value))}
                  className="w-full accent-orange-500 cursor-pointer"
                />
              </div>

              <div>
                <div className="flex justify-between text-xs font-bold text-slate-500 mb-1.5">
                  <span>WEEKLY HOURS LOCKED IN DOCS / WORKER</span>
                  <span className="text-emerald-500">{calcWeeklyHoursSpent} Hours</span>
                </div>
                <input 
                  type="range" 
                  min="5" 
                  max="40" 
                  value={calcWeeklyHoursSpent} 
                  onChange={(e) => setCalcWeeklyHoursSpent(parseInt(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
              </div>

              <div>
                <div className="flex justify-between text-xs font-bold text-slate-500 mb-1.5">
                  <span>AVERAGE HOURLY DEPLOYED RATE (₹ INR)</span>
                  <span className="text-blue-500">₹{calcHourlyRate}/Hr</span>
                </div>
                <input 
                  type="range" 
                  min="25" 
                  max="2500" 
                  step="50"
                  value={calcHourlyRate} 
                  onChange={(e) => setCalcHourlyRate(parseInt(e.target.value))}
                  className="w-full accent-blue-500 cursor-pointer"
                />
              </div>
            </div>
          </div>
          
          {/* ROI DISPLAY */}
          <div className="bg-slate-900 text-white p-6 rounded-3xl border border-slate-800 shadow-xl flex flex-col justify-between h-full min-h-[350px] relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl"></div>
            
            <div className="space-y-4">
              <span className="text-xs uppercase font-bold tracking-widest text-emerald-400">ESTIMATED DIRECT SAVINGS YEAR 1+</span>
              
              <div className="border-l-4 border-emerald-500 pl-4 space-y-1">
                <span className="text-sm text-slate-400">Total Money Saved Annually:</span>
                <h4 className="text-4xl font-black text-white">₹{currentSavings.moneySaved}</h4>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-800">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Admin Hours Restored</span>
                  <span className="text-xl font-bold text-emerald-400">{currentSavings.hoursSaved} Hours</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Paper Sheets Saved</span>
                  <span className="text-xl font-bold text-orange-400">{currentSavings.paperSaved} Sheets</span>
                </div>
              </div>
            </div>

            <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-2xl text-xs text-emerald-400 mt-6">
              📌 <strong>Client Case Study:</strong> At CISPL fabrication unit, paper consumption was reduced by <strong>22%</strong> immediately, while output reports compiled in <strong>&lt;5 minutes</strong> vs 20 minutes average.
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Partner with DiGiOPS Today",
      subtitle: "Let's build a bespoke digital operating framework for your plant",
      category: "Conclusion",
      content: (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full items-center">
          <div className="space-y-5">
            <h3 className="text-3xl font-black tracking-tight text-slate-800 dark:text-white">Let's Digitalize Your Plant Operations</h3>
            <p className="text-slate-600 dark:text-slate-300 text-base">
              A custom-tailored ERP deployment is not a technical expense—it is a long-term strategic advantage that improves cash flow, simplifies compliance, and guarantees full trace accountability.
            </p>
            
            <div className="space-y-3 pt-3">
              <div className="p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-3">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-500/10 text-orange-500 rounded-lg flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="font-bold text-sm text-slate-800 dark:text-slate-200">Garv Jhangiani</h5>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Chief Systems Architect & Developer</p>
                </div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-500/10 text-emerald-500 rounded-lg flex items-center justify-center shrink-0">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <h5 className="font-bold text-sm text-slate-800 dark:text-slate-200">Deploy Phase 1 Core Modules</h5>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Fixed onboarding schedule with verified weekly checkpoints</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* CONTACT OR INQUIRY BLOCK */}
          <div className="bg-gradient-to-tr from-slate-900 to-slate-800 p-6 rounded-3xl border border-slate-700 shadow-xl text-white">
            <h4 className="text-xs uppercase font-bold tracking-widest text-emerald-400 mb-4">DIRECT CONTACT CHANNELS</h4>
            <div className="space-y-4">
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider">OFFICIAL WEBSITE</span>
                <p className="text-base font-bold text-white font-mono">garvjhangiani.com</p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider">SECURE DIRECT CALL</span>
                <p className="text-base font-bold text-white font-mono">+91 8169226866</p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider">DIRECT EMAIL INQUIRIES</span>
                <p className="text-base font-bold text-white font-mono text-orange-400">garvjhangiani@gmail.com</p>
              </div>
              <div className="pt-4 border-t border-slate-700 flex justify-between items-center text-xs text-slate-400">
                <span>DiGiOPS Enterprise Deployments</span>
                <span className="bg-emerald-500/20 text-emerald-400 font-bold px-2 py-0.5 rounded uppercase">V1.0 Ready</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
  ];

  const nextSlide = useCallback(() => currentSlide < slidesData.length - 1 && setCurrentSlide(c => c + 1), [currentSlide, slidesData.length]);
  const prevSlide = useCallback(() => currentSlide > 0 && setCurrentSlide(c => c - 1), [currentSlide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, nextSlide, prevSlide]);

  return (
    <div className={`${isDarkMode ? 'dark' : ''} min-h-screen bg-slate-50 text-slate-800 dark:bg-slate-900 dark:text-slate-100 flex flex-col justify-between overflow-x-hidden antialiased font-sans transition-colors duration-300`}>
      
      {/* HEADER SECTION */}
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-6 py-4 flex justify-between items-center sticky top-0 z-50 transition-colors duration-300">
        <DiGiOPSLogo />
        
        {/* Pitch Controls */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border cursor-pointer bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 shadow-sm"
          >
            {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span className="hidden sm:inline">{isDarkMode ? "Light Mode" : "Dark Mode"}</span>
          </button>

          <span className="text-xs bg-white border-slate-200 text-slate-600 dark:bg-slate-800 dark:border-slate-700/60 dark:text-slate-300 px-3 py-1.5 rounded-xl font-mono border transition-colors duration-300">
            {currentSlide + 1} / {slidesData.length}
          </span>
        </div>
      </header>

      {/* CORE PRESENTATION BODY */}
      <main className="flex-1 flex flex-col lg:flex-row gap-6 p-6 max-w-[1400px] mx-auto w-full items-stretch">
        
        {/* PRESENTATION SLIDE SCREEN */}
        <div className="flex-1 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/80 shadow-xl rounded-3xl p-6 md:p-8 flex flex-col justify-between transition-all relative overflow-hidden min-h-[550px]">
          
          <div className="absolute top-8 right-8 opacity-10 pointer-events-none scale-75 origin-top-right">
          </div>

          <div className="text-xs font-black uppercase tracking-widest text-orange-500 dark:text-orange-400 mb-2">
            {slidesData[currentSlide].category}
          </div>

          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-800 dark:text-white leading-tight">
              {slidesData[currentSlide].title}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {slidesData[currentSlide].subtitle}
            </p>
          </div>

          <div className="flex-1 text-slate-800 dark:text-slate-100 text-sm md:text-base flex flex-col justify-center">
            {slidesData[currentSlide].content}
          </div>

          {/* Slide Navigation Buttons */}
          <div className="flex justify-between items-center border-t border-slate-100 dark:border-slate-800 pt-6 mt-8">
            <button 
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                currentSlide === 0 
                  ? 'text-slate-400 border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 cursor-not-allowed opacity-50' 
                  : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Back</span>
            </button>

            <div className="hidden md:flex gap-1.5">
              {slidesData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                    currentSlide === index 
                      ? 'bg-orange-500 w-6' 
                      : 'bg-slate-300 dark:bg-slate-700 hover:bg-slate-400'
                  }`}
                  title={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <button 
              onClick={nextSlide}
              disabled={currentSlide === slidesData.length - 1}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                currentSlide === slidesData.length - 1 
                  ? 'text-slate-400 border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 cursor-not-allowed opacity-50' 
                  : 'bg-orange-500 border-orange-600 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20'
              }`}
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 py-3 flex flex-col sm:flex-row justify-between items-center text-xs text-slate-500 gap-2 shrink-0 transition-colors duration-300">
        <div className="flex items-center gap-3">
          <span>Enterprise Custom Development Presentation</span>
          <span className="text-slate-300 dark:text-slate-700">|</span>
          <span className="font-mono text-[10px] text-orange-500 dark:text-orange-400">Author: Garv Jhangiani</span>
        </div>
        <div className="flex items-center gap-3">
          <span>Target Market: Manufacturing, Fabrication & Repairing</span>
          <span className="bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold text-[9px] px-2 py-0.5 rounded-full uppercase">Verified V1.0</span>
        </div>
      </footer>
    </div>
  );
}